import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { useMemo } from 'react'

import { VODOPAD_ENDPOINT } from '@/constants/constants'
import { useVmConfigurations } from '@/hooks/useVmConfigurations'
import { ensureArray } from '@/types/api'

import type { PriceEntry } from './priceUtils'
import { formatVmConfig, PRICE_ACTIVE_URLS, toPriceEntry } from './priceUtils'

type VmActive = components['schemas']['AdminVmPriceInfoDto']
type StorageActive = components['schemas']['AdminStoragePriceInfoDto']
type IpActive = components['schemas']['AdminPublicIpPriceInfoDto']
type VmConfigurationDto = components['schemas']['VmConfigurationDto']

export interface PriceGroup {
  key: string
  clusterId: string
  userGroup: string
  resourceLabel: string
  currentPrice: PriceEntry | null
}

function groupVm(
  data: VmActive[],
  configMap: Map<string, VmConfigurationDto>,
): PriceGroup[] {
  return data.map((s) => {
    const cfg = configMap.get(s.vmTypeId.vmConfigurationId)
    const label = cfg ? formatVmConfig(cfg) : s.vmTypeId.vmConfigurationId
    const entry = toPriceEntry(s.priceInfo)
    return {
      key: `${s.vmTypeId.clusterId}:${s.vmTypeId.vmConfigurationId}`,
      clusterId: s.vmTypeId.clusterId,
      userGroup: s.vmTypeId.userGroup,
      resourceLabel: label,
      currentPrice: entry,
    }
  })
}

function groupStorage(data: StorageActive[]): PriceGroup[] {
  return data.map((s) => {
    const t = s.storageTypeId
    const label = `${t.storageType}${t.replicated ? ' (replicated)' : ''}`
    const entry = toPriceEntry(s.priceInfo)
    return {
      key: `${t.clusterId}:${t.storageType}:${t.replicated}`,
      clusterId: t.clusterId,
      userGroup: t.userGroup,
      resourceLabel: label,
      currentPrice: entry,
    }
  })
}

function groupIp(data: IpActive[]): PriceGroup[] {
  return data.map((s) => {
    const t = s.publicIpTypeId
    const label = t.addressType === 'V4' ? 'IPv4' : 'IPv6'
    const entry = toPriceEntry(s.priceInfo)
    return {
      key: `${t.clusterId}:${t.addressType}`,
      clusterId: t.clusterId,
      userGroup: t.userGroup,
      resourceLabel: label,
      currentPrice: entry,
    }
  })
}

const ACTIVE_STALE_TIME = 60_000

export function useGroupPrices(groupName?: string, clusterId?: string) {
  const query = groupName ? { userGroup: groupName } : {}

  const vmQuery = useCustom<VmActive[]>({
    url: `${VODOPAD_ENDPOINT}${PRICE_ACTIVE_URLS.vm}`,
    method: 'get',
    config: { query },
    queryOptions: { staleTime: ACTIVE_STALE_TIME },
  })
  const storageQuery = useCustom<StorageActive[]>({
    url: `${VODOPAD_ENDPOINT}${PRICE_ACTIVE_URLS.storage}`,
    method: 'get',
    config: { query },
    queryOptions: { staleTime: ACTIVE_STALE_TIME },
  })
  const ipQuery = useCustom<IpActive[]>({
    url: `${VODOPAD_ENDPOINT}${PRICE_ACTIVE_URLS['public-ip']}`,
    method: 'get',
    config: { query },
    queryOptions: { staleTime: ACTIVE_STALE_TIME },
  })

  const { configurations } = useVmConfigurations()
  const configMap = useMemo(
    () => new Map(configurations.map((c) => [c.id, c])),
    [configurations],
  )

  const vmGroups = useMemo(
    () => groupVm(ensureArray<VmActive>(vmQuery.query.data?.data), configMap),
    [vmQuery.query.data?.data, configMap],
  )
  const storageGroups = useMemo(
    () =>
      groupStorage(ensureArray<StorageActive>(storageQuery.query.data?.data)),
    [storageQuery.query.data?.data],
  )
  const ipGroups = useMemo(
    () => groupIp(ensureArray<IpActive>(ipQuery.query.data?.data)),
    [ipQuery.query.data?.data],
  )

  const filteredVm = useMemo(
    () =>
      clusterId ? vmGroups.filter((g) => g.clusterId === clusterId) : vmGroups,
    [vmGroups, clusterId],
  )
  const filteredStorage = useMemo(
    () =>
      clusterId
        ? storageGroups.filter((g) => g.clusterId === clusterId)
        : storageGroups,
    [storageGroups, clusterId],
  )
  const filteredIp = useMemo(
    () =>
      clusterId ? ipGroups.filter((g) => g.clusterId === clusterId) : ipGroups,
    [ipGroups, clusterId],
  )

  const loading =
    vmQuery.query.isLoading ||
    storageQuery.query.isLoading ||
    ipQuery.query.isLoading

  const refetchAll = () => {
    void vmQuery.query.refetch()
    void storageQuery.query.refetch()
    void ipQuery.query.refetch()
  }

  return {
    vmGroups: filteredVm,
    storageGroups: filteredStorage,
    ipGroups: filteredIp,
    loading,
    refetchAll,
  }
}
